import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dropdown } from 'antd';
import { HeaderItem } from '@/pages/Data/types';
import { GetShipmentHeaders, updateShipmentColumns } from '@/apis/shipment';
import ReportPanelContent from '@/pages/Data/components/ReportPanelContent';
import { ColumnsIcon } from '@/assets/icons/icons';
import { ErrorLike } from '@/apis/types';

interface HeaderResData {
  header?: HeaderItem[];
}

interface Props {
  token: string;
  activeTabId: string;
  unauthorize: (error: ErrorLike) => void;
  onChange?: (headers: HeaderItem[]) => void;
  onColumnsSaved?: () => Promise<unknown> | void;
  dropdownMaxHeight?: number | string;
}

const COLUMNS_DROPDOWN_VIEWPORT_BUFFER = 16;
const COLUMNS_DROPDOWN_MIN_HEIGHT = 240;
const COLUMNS_DROPDOWN_MAX_HEIGHT = 550;

const ShipmentColumnsDropdown: React.FC<Props> = ({
  token,
  activeTabId,
  unauthorize,
  onChange,
  onColumnsSaved,
  dropdownMaxHeight,
}) => {
  const [open, setOpen] = useState(false);
  const [availableHeight, setAvailableHeight] = useState<number | undefined>();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();

  const columnsQueryKey = useMemo(
    () => ['shipment-columns', activeTabId],
    [activeTabId]
  );

  const columnsQuery = useQuery({
    queryKey: columnsQueryKey,
    queryFn: async (): Promise<HeaderItem[]> => {
      const params = `tab_id=${activeTabId}`;
      const res = await GetShipmentHeaders(token, params);
      const data = res?.data?.data ?? null;

      return (data?.columns ?? []) as HeaderItem[];
    },
    enabled: Boolean(token && activeTabId),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (columnsQuery.error) {
      unauthorize(columnsQuery.error as ErrorLike);
    }
  }, [columnsQuery.error, unauthorize]);

  useEffect(() => {
    if (columnsQuery.data?.length) {
      onChange?.(columnsQuery.data);
    }
  }, [columnsQuery.data, onChange]);

  const updateColumnsMutation = useMutation({
    mutationFn: (updatedColumns: HeaderItem[]) =>
      updateShipmentColumns(token, {
        tab_id: activeTabId,
        columns: updatedColumns,
      }),
    onMutate: async (updatedColumns) => {
      await queryClient.cancelQueries({ queryKey: columnsQueryKey });
      const previousColumns =
        queryClient.getQueryData<HeaderItem[]>(columnsQueryKey) ?? [];

      queryClient.setQueryData(columnsQueryKey, updatedColumns);
      onChange?.(updatedColumns);

      return { previousColumns };
    },
    onError: (error, _updatedColumns, context) => {
      const previousColumns = context?.previousColumns ?? [];
      queryClient.setQueryData(columnsQueryKey, previousColumns);
      onChange?.(previousColumns);
      unauthorize(error as ErrorLike);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: columnsQueryKey });
      if (onColumnsSaved) {
        await onColumnsSaved();
        return;
      }

      await queryClient.invalidateQueries({ queryKey: ['shipment-report'] });
    },
  });

  const handleUpdatedHeaders = (updatedHeaderList: HeaderItem[]) => {
    setOpen(false);
    updateColumnsMutation.mutate(updatedHeaderList);
  };

  const computeAvailableHeight = useCallback(() => {
    if (dropdownMaxHeight || typeof window === 'undefined') return;

    const triggerRect = triggerRef.current?.getBoundingClientRect();
    const viewportTop = triggerRect?.bottom ?? 0;
    const space =
      window.innerHeight - viewportTop - COLUMNS_DROPDOWN_VIEWPORT_BUFFER;

    setAvailableHeight(
      Math.max(
        COLUMNS_DROPDOWN_MIN_HEIGHT,
        Math.min(COLUMNS_DROPDOWN_MAX_HEIGHT, Math.floor(space))
      )
    );
  }, [dropdownMaxHeight]);

  useLayoutEffect(() => {
    if (!open) return;
    computeAvailableHeight();
  }, [computeAvailableHeight, open]);

  useEffect(() => {
    if (!open) return;

    window.addEventListener('resize', computeAvailableHeight);
    window.addEventListener('scroll', computeAvailableHeight, true);

    return () => {
      window.removeEventListener('resize', computeAvailableHeight);
      window.removeEventListener('scroll', computeAvailableHeight, true);
    };
  }, [computeAvailableHeight, open]);

  const columnsHeaders: HeaderResData = {
    header: columnsQuery.data ?? [],
  };
  const resolvedDropdownHeight = dropdownMaxHeight ?? availableHeight;

  const menu = (
    <div
      className='column-selection-dropdown'
      style={
        resolvedDropdownHeight
          ? {
              height: resolvedDropdownHeight,
              maxHeight: resolvedDropdownHeight,
            }
          : undefined
      }
    >
      <ReportPanelContent
        appliedHeaderList={columnsHeaders?.header ?? []}
        sendHeadersToParent={handleUpdatedHeaders}
        isShipmentColumns={true}
      />
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => menu}
      trigger={['click']}
      placement='bottomRight'
      open={open}
      onOpenChange={(visible) => {
        if (visible) {
          computeAvailableHeight();
        }
        setOpen(visible);
      }}
    >
      <button
        ref={triggerRef}
        type='button'
        className='zl-btn bg-f3 rounded-full flex-center y'
      >
        <ColumnsIcon /> <span className={'lg-d-none'}>Columns</span>
      </button>
    </Dropdown>
  );
};

export default ShipmentColumnsDropdown;
